// src/components/pv/PvOrientationDiagram.jsx
// Live geometry preview for the PV System > System & Orientation tab.
//
// Two views, both redrawn as the fields change:
//   - side elevation: the tilt angle off horizontal
//   - compass plan:   the azimuth the modules face
//
// Azimuth follows the convention stated on the form itself and used by the
// engine: 0 = North, 90 = East, 180 = South, 270 = West (clockwise from north).
// Plain inline SVG, so there is no charting dependency and it inherits the MUI
// palette in both light and dark mode. The captions are deliberately DOM text
// rather than <text> inside the SVG: in-SVG labels scale with the viewBox, so
// their clearance from the compass letters drifted with the page zoom.
import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import { useIsGreek } from "../../context/LanguageContext";

const COMPASS_16 = [
  "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW",
];
// The same points as the Greek compass writes them (Β, Α, Ν, Δ).
const COMPASS_16_EL = [
  "Β", "ΒΒΑ", "ΒΑ", "ΑΒΑ", "Α", "ΑΝΑ", "ΝΑ", "ΝΝΑ",
  "Ν", "ΝΝΔ", "ΝΔ", "ΔΝΔ", "Δ", "ΔΒΔ", "ΒΔ", "ΒΒΔ",
];

const norm360 = (deg) => ((deg % 360) + 360) % 360;

const MOUNT_LABELS = {
  fixed: "Fixed mount",
  single_axis: "Single-axis tracking",
  dual_axis: "Dual-axis tracking",
};

export function cardinalOf(azimuth, greek = false) {
  const idx = Math.round(norm360(azimuth) / 22.5) % 16;
  return (greek ? COMPASS_16_EL : COMPASS_16)[idx];
}

const num = (v) => (v === "" || v == null || !Number.isFinite(Number(v)) ? null : Number(v));

export default function PvOrientationDiagram({ tilt, azimuth, latitude, mountType }) {
  const theme = useTheme();
  const greek = useIsGreek();
  const tr = (el, en) => (greek ? el : en);
  const t = theme.palette;

  const tiltVal = num(tilt);
  const azimVal = num(azimuth);
  const lat = num(latitude);
  const isTracking = mountType === "single_axis" || mountType === "dual_axis";

  // Clamp only for drawing; the numbers shown are always what the user entered.
  const tiltDraw = Math.min(90, Math.max(0, tiltVal ?? 0));
  const azimDraw = norm360(azimVal ?? 180);

  const line = t.divider;
  const muted = t.text.secondary;
  const panel = t.primary.main;
  const accent = t.warning.main;
  // The panel colour behind the angle label, so the halo matches whatever
  // surface the diagram is sitting on in either theme.
  const surface = t.background.paper;

  // ---------- side elevation ----------
  const GROUND_Y = 116;
  const PIVOT_X = 62;
  const PANEL_LEN = 132;
  const rad = (tiltDraw * Math.PI) / 180;
  const tipX = PIVOT_X + PANEL_LEN * Math.cos(rad);
  const tipY = GROUND_Y - PANEL_LEN * Math.sin(rad);
  const ARC_R = 42;
  const arcEndX = PIVOT_X + ARC_R * Math.cos(rad);
  const arcEndY = GROUND_Y - ARC_R * Math.sin(rad);

  /**
   * Where the angle's number goes. ONE RULE, TWO PLACES, NEVER ON THE PANEL.
   *
   * The wedge between the ground and the module is the natural home for the
   * number -- it is what the number measures -- but it only has room once the
   * angle is wide enough. Below that the label goes ABOVE the module instead,
   * on the far side from the wedge. Two earlier placements each failed at some
   * angle: a fixed point near the ground vanished under a shallow panel, and an
   * offset that was meant to be the panel's normal was not one, so at 43
   * degrees the number sat on the module itself.
   *
   * Inside: on the wedge's bisector at radius 74, which keeps the label the same
   * distance from the ground and from the module. Above: a point on the module
   * at radius 54, pushed 26px along the true upward normal (-sin, -cos).
   *
   * The four numbers were not guessed. Every integer tilt from 0 to 90 was
   * checked for the distance from the nearest corner of a 26x13 text box to the
   * 9px module and to the ground, and this combination is the one that keeps
   * that distance largest at its worst angle: 8px, at the switch-over. The
   * earlier hand-picked values grazed the module between 23 and 34 degrees.
   */
  const LABEL_INSIDE_MIN_DEG = 42;
  const labelInside = tiltDraw >= LABEL_INSIDE_MIN_DEG;
  const labelX = labelInside
    ? PIVOT_X + 74 * Math.cos(rad / 2)
    : PIVOT_X + 54 * Math.cos(rad) - 26 * Math.sin(rad);
  const labelY = labelInside
    ? GROUND_Y - 74 * Math.sin(rad / 2) + 4
    : GROUND_Y - 54 * Math.sin(rad) - 26 * Math.cos(rad) + 4;

  // ---------- compass plan ----------
  const CX = 100;
  const CY = 100;
  const RING_R = 74;
  const aRad = (azimDraw * Math.PI) / 180;
  // Screen y grows downward, so north (0 deg) is -y.
  const faceX = CX + RING_R * 0.82 * Math.sin(aRad);
  const faceY = CY - RING_R * 0.82 * Math.cos(aRad);

  // In the northern hemisphere the modules should look south, and vice versa.
  // Anything more than 90 deg off that is pointing away from the sun's arc.
  const idealAz = lat == null ? 180 : lat >= 0 ? 180 : 0;
  const offIdeal =
    azimVal == null ? null : Math.abs(((norm360(azimVal - idealAz) + 180) % 360) - 180);
  const facingAway = offIdeal != null && offIdeal > 90;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        {/* ---------------- tilt: side elevation ---------------- */}
        <Box sx={{ flex: "0 0 auto" }}>
          <svg viewBox="0 0 260 136" width="260" height="136" role="img"
               aria-label={`Side view: panel tilted ${tiltDraw} degrees from horizontal`}>
            {/* ground */}
            <line x1="18" y1={GROUND_Y} x2="242" y2={GROUND_Y} stroke={line} strokeWidth="2" />
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <line
                key={i}
                x1={22 + i * 28}
                y1={GROUND_Y + 2}
                x2={14 + i * 28}
                y2={GROUND_Y + 11}
                stroke={line}
                strokeWidth="1.5"
              />
            ))}

            {/* horizontal reference + angle arc */}
            <line
              x1={PIVOT_X}
              y1={GROUND_Y}
              x2={PIVOT_X + ARC_R + 16}
              y2={GROUND_Y}
              stroke={muted}
              strokeDasharray="3 3"
              strokeWidth="1"
            />
            <path
              d={`M ${PIVOT_X + ARC_R} ${GROUND_Y} A ${ARC_R} ${ARC_R} 0 0 0 ${arcEndX} ${arcEndY}`}
              fill="none"
              stroke={accent}
              strokeWidth="1.6"
            />
            {/* The angle. See labelX / labelY above for the placement rule. */}
            <text
              x={labelX}
              y={labelY}
              textAnchor="middle"
              fill="none"
              stroke={surface}
              strokeWidth="3.5"
              strokeLinejoin="round"
              fontSize="13"
              fontWeight="700"
            >
              {tiltVal == null ? "—" : `${tiltVal}°`}
            </text>
            <text
              x={labelX}
              y={labelY}
              textAnchor="middle"
              fill={accent}
              fontSize="13"
              fontWeight="700"
            >
              {tiltVal == null ? "—" : `${tiltVal}°`}
            </text>

            {/* the module */}
            <line
              x1={PIVOT_X}
              y1={GROUND_Y}
              x2={tipX}
              y2={tipY}
              stroke={panel}
              strokeWidth="9"
              strokeLinecap="round"
            />
            {/* mounting leg */}
            <line x1={tipX} y1={tipY} x2={tipX} y2={GROUND_Y} stroke={muted} strokeWidth="1.5"
                  strokeDasharray="3 3" />

            {/* sun, kept normal-ish to the module face */}
            <g transform={`translate(${tipX - 34} ${Math.max(22, tipY - 40)})`}>
              <circle r="9" fill={accent} opacity="0.9" />
              {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
                <line
                  key={a}
                  x1={13 * Math.cos((a * Math.PI) / 180)}
                  y1={13 * Math.sin((a * Math.PI) / 180)}
                  x2={17 * Math.cos((a * Math.PI) / 180)}
                  y2={17 * Math.sin((a * Math.PI) / 180)}
                  stroke={accent}
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              ))}
            </g>

          </svg>
          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", color: muted, mt: 1.25 }}
          >
            Side view — tilt from horizontal
          </Typography>
        </Box>

        {/* ---------------- azimuth: compass plan ---------------- */}
        <Box sx={{ flex: "0 0 auto" }}>
          <svg viewBox="0 0 200 200" width="190" height="190" role="img"
               aria-label={`Plan view: modules facing ${azimDraw} degrees, ${cardinalOf(azimDraw)}`}>
            <circle cx={CX} cy={CY} r={RING_R} fill="none" stroke={line} strokeWidth="1.5" />
            {Array.from({ length: 12 }).map((_, i) => {
              const a = (i * 30 * Math.PI) / 180;
              return (
                <line
                  key={i}
                  x1={CX + (RING_R - 6) * Math.sin(a)}
                  y1={CY - (RING_R - 6) * Math.cos(a)}
                  x2={CX + RING_R * Math.sin(a)}
                  y2={CY - RING_R * Math.cos(a)}
                  stroke={line}
                  strokeWidth="1.2"
                />
              );
            })}

            <text x={CX} y={CY - RING_R - 6} fill={muted} fontSize="12" textAnchor="middle">N</text>
            <text x={CX + RING_R + 10} y={CY + 4} fill={muted} fontSize="12" textAnchor="middle">E</text>
            <text x={CX} y={CY + RING_R + 15} fill={muted} fontSize="12" textAnchor="middle">S</text>
            <text x={CX - RING_R - 10} y={CY + 4} fill={muted} fontSize="12" textAnchor="middle">W</text>

            {/* facing direction */}
            <line
              x1={CX}
              y1={CY}
              x2={faceX}
              y2={faceY}
              stroke={accent}
              strokeWidth="2"
              markerEnd="url(#pvo-arrow2)"
            />
            <defs>
              <marker id="pvo-arrow2" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 z" fill={accent} />
              </marker>
            </defs>

            {/* the array seen from above: long axis square to the facing direction */}
            <g transform={`rotate(${azimDraw} ${CX} ${CY})`}>
              <rect
                x={CX - 34}
                y={CY - 12}
                width="68"
                height="26"
                rx="3"
                fill={panel}
                opacity="0.85"
              />
              {[1, 2, 3].map((i) => (
                <line
                  key={i}
                  x1={CX - 34 + i * 17}
                  y1={CY - 12}
                  x2={CX - 34 + i * 17}
                  y2={CY + 14}
                  stroke={t.background.paper}
                  strokeWidth="1.2"
                  opacity="0.8"
                />
              ))}
            </g>

          </svg>
          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", color: muted, mt: 1.25 }}
          >
            Plan view — azimuth faced
          </Typography>
        </Box>

        {/* ---------------- readout ---------------- */}
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={<><span>Tilt</span>{` ${tiltVal == null ? "—" : `${tiltVal}°`}`}</>}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              size="small"
              label={
                <><span>Azimuth</span>{azimVal == null
                  ? " —"
                  : ` ${azimVal}° · ${cardinalOf(azimVal, greek)}`}</>
              }
              sx={{ fontWeight: 700 }}
            />
            {mountType ? (
              <Chip
                size="small"
                variant="outlined"
                label={MOUNT_LABELS[mountType] || mountType}
              />
            ) : null}
          </Stack>

          {isTracking && (
            <Typography variant="caption" sx={{ color: muted }}>
              {MOUNT_LABELS[mountType]} follows the sun through the day, so a single
              fixed tilt and azimuth do not apply — the view above is indicative only.
            </Typography>
          )}
          {!isTracking && tiltVal === 0 && (
            <Typography variant="caption" sx={{ color: muted }}>
              A flat array (0°) sheds less rain and soils faster.
            </Typography>
          )}
          {facingAway && !isTracking && (
            // One sentence in the screen's language: pieced together from
            // fragments it came out as a word-for-word Greek that read wrong.
            <Typography variant="caption" sx={{ color: t.warning.main, fontWeight: 600 }} data-no-translate>
              {tr(
                `Προσανατολισμός προς ${cardinalOf(azimVal, true)} — ${offIdeal.toFixed(0)}° από τον ${idealAz === 180 ? "νότο" : "βορρά"}· η συστοιχία στρέφεται μακριά από την πορεία του ήλιου και η απόδοση πέφτει απότομα.`,
                `Facing ${cardinalOf(azimVal)} — ${offIdeal.toFixed(0)}° away from ${idealAz === 180 ? "south" : "north"}, so the array turns from the sun's arc and yield drops sharply.`,
              )}
            </Typography>
          )}
          {lat != null && !isTracking && (
            <Typography variant="caption" sx={{ color: muted }}>
              Rule of thumb for annual yield: tilt ≈ latitude ({Math.abs(lat).toFixed(1)}°),
              facing {idealAz === 180 ? "south" : "north"}. Use <b>Optimize</b> for the
              site-specific answer.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
